import { LoggedUser } from './session-decode';
import { isIntegrator } from './user-type';

/** Destinos possíveis do redirect automático pós-login. */
export const POST_LOGIN_ROUTES = {
  selectAccount: '/conta/select-conta',
  home: '/home',
  wizard: '/wizard',
  integratorHome: '/integrador/inicio',
  integratorList: '/integrador/list-integrador',
} as const;

/**
 * Entradas da decisão de redirect que não vêm do usuário decodificado.
 *
 * O `LoginService` do Angular resolve esses valores de forma assíncrona (config,
 * pessoa logada) e via estado mutável (`accountSelected`, `redirecAccountList`,
 * `doNotRedirectAccountList`, `pathIntegrador`). Aqui eles entram como dados já
 * resolvidos para manter a decisão pura.
 */
export interface PostLoginContext {
  /**
   * Uma conta específica já foi escolhida — pelo seletor de contas ou pela
   * auto-seleção quando o usuário tem exatamente uma conta. Espelha o parâmetro
   * `accountSelected` de `goHome`; o chamador é responsável por reproduzir a
   * auto-seleção de conta única do `registerUser` antes de decidir.
   */
  accountSelected?: boolean;
  /** Suprime qualquer redirect automático (`doNotRedirectAccountList`). */
  suppressRedirect?: boolean;
  /** Acesso do usuário (ou da conta única) bloqueado — não redireciona. */
  acessoBloqueado?: boolean;
  /** Rota guardada para retornar após a seleção de conta (`redirecAccountList`). */
  redirectAccountList?: string | null;
  /** Rota de integrador guardada em storage (`pathIntegrador`). */
  pathIntegrador?: string | null;
  /** `pessoa.primeiroAcesso` — ausente/`true` leva ao wizard quando os dados estão bloqueados. */
  primeiroAcesso?: boolean | null;
  /** `config.editarMeusDados` — quando `true`, o primeiro acesso vai ao wizard. */
  editarMeusDados?: boolean;
}

/**
 * Destino do redirect automático pós-login. Espelha a árvore de decisão de
 * `LoginService.goHome`/`registerUser`: seletor de contas para múltiplas contas,
 * wizard vs. home para o morador em primeiro acesso, e as home de integrador e
 * distribuidor. Retorna `null` quando nenhum redirect deve ocorrer (sem usuário,
 * acesso bloqueado ou redirect suprimido).
 */
export function resolvePostLoginRedirect(
  user: LoggedUser | null,
  ctx: PostLoginContext = {},
): string | null {
  if (!user) {
    return null;
  }

  // Acesso bloqueado apenas exibe aviso no legado; nunca navega.
  if (ctx.acessoBloqueado) {
    return null;
  }

  if (ctx.suppressRedirect) {
    return null;
  }

  const contas = user.contas ?? [];

  // Múltiplas contas sem uma selecionada: a decisão de home é adiada para depois
  // do seletor de contas.
  if (contas.length > 1 && !ctx.accountSelected) {
    return POST_LOGIN_ROUTES.selectAccount;
  }

  // Morador (não integrador) com pelo menos uma conta: wizard no primeiro acesso
  // com edição de dados bloqueada, caso contrário a home da conta.
  if (!isIntegrator(user) && contas.length > 0) {
    return resolveTenantHome(ctx);
  }

  // Integrador ou distribuidor (sem contas).
  if (ctx.accountSelected) {
    return ctx.redirectAccountList || POST_LOGIN_ROUTES.home;
  }

  if (isIntegrator(user)) {
    return ctx.redirectAccountList || POST_LOGIN_ROUTES.integratorHome;
  }

  return ctx.pathIntegrador || POST_LOGIN_ROUTES.integratorList;
}

function resolveTenantHome(ctx: PostLoginContext): string {
  const emPrimeiroAcesso = ctx.primeiroAcesso == null || ctx.primeiroAcesso === true;
  if (ctx.editarMeusDados && emPrimeiroAcesso) {
    return POST_LOGIN_ROUTES.wizard;
  }
  return POST_LOGIN_ROUTES.home;
}
